import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';

// Rarity translation map
const RARITY_TRANSLATIONS = {
  'common': 'comune',
  'rare': 'rara',
  'Common': 'Comune',
  'Rare': 'Rara',
  'rainbow': 'arcobaleno',
  'Rainbow': 'Arcobaleno',
  'legendary': 'leggendaria',
  'Legendary': 'Leggendaria',
  'epic': 'epica',
  'Epic': 'Epica',
  'uncommon': 'non comune',
  'Uncommon': 'Non Comune'
};

// Attribute translation map
const ATTRIBUTE_TRANSLATIONS = {
  'LIGHT': 'Luce',
  'DARK': 'Tenebre',
  'EARTH': 'Terra',
  'WATER': 'Acqua',
  'FIRE': 'Fuoco',
  'WIND': 'Vento',
  'DIVINE': 'Divino',
  'light': 'luce',
  'dark': 'tenebre',
  'earth': 'terra',
  'water': 'acqua',
  'fire': 'fuoco',
  'wind': 'vento',
  'divine': 'divino'
};

const translateRarity = (rarity) => {
  return RARITY_TRANSLATIONS[rarity] || rarity;
};

const translateAttribute = (attribute) => {
  return ATTRIBUTE_TRANSLATIONS[attribute] || attribute;
};

function AuctionCard({ auction, user, token, socket, onBidSuccess }) {
  const [bidAmount, setBidAmount] = useState('');
  const [timeLeft, setTimeLeft] = useState(auction.end_time ? '' : 'Senza limite');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (socket) {
      socket.emit('join_auction', auction.id);
    }

    return () => {
      if (socket) {
        socket.emit('leave_auction', auction.id);
      }
    };
  }, [socket, auction.id]);

  useEffect(() => {
    if (!auction.end_time) {
      setTimeLeft('Senza limite');
      return;
    }

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const endTime = new Date(auction.end_time).getTime();
      const difference = endTime - now;

      if (difference > 0) {
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        setTimeLeft(`${minutes}m ${seconds}s`);
      } else {
        setTimeLeft('Terminata');
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [auction.end_time]);

  const handleBid = async (e) => {
    e.preventDefault();
    
    const bid = parseFloat(bidAmount);
    if (!bid || bid <= auction.current_bid) {
      setError(`L'offerta deve essere superiore a ${auction.current_bid} patapizze`);
      return;
    }
    
    if (bid > user.patapizze) {
      setError('Patapizze insufficienti');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(
        `${API_URL}/api/auctions/${auction.id}/bid`,
        { bid_amount: bid },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setBidAmount('');
      
      const updatedUser = { ...user, patapizze: user.patapizze };
      onBidSuccess(updatedUser);
    } catch (error) {
      setError(error.response?.data?.error || 'Errore nell\'effettuare l\'offerta');
    } finally {
      setLoading(false);
    }
  };

  const cardMetadata = auction.metadata ? (typeof auction.metadata === 'string' ? JSON.parse(auction.metadata) : auction.metadata) : {};

  return (
    <div className="auction-card">
      <div className="auction-info">
        <h3>{auction.name}</h3>
        
        {cardMetadata.effect && (
          <div className="card-effect-text">
            {cardMetadata.effect}
          </div>
        )}
        
        <div className="card-metadata">
          {(cardMetadata.atk || cardMetadata.def) && (
            <div className="card-stats">
              {cardMetadata.atk && <span><strong>ATK:</strong> {cardMetadata.atk}</span>}
              {cardMetadata.def && <span><strong>DEF:</strong> {cardMetadata.def}</span>}
            </div>
          )}
          
          {cardMetadata.level && (
            <div className="card-info">
              <strong>Livello:</strong> {cardMetadata.level} | <strong>Tipo:</strong> {cardMetadata.type}
            </div>
          )}
          
          {cardMetadata.attribute && (
            <div className="card-info">
              <strong>Attributo:</strong> {translateAttribute(cardMetadata.attribute)}
              {cardMetadata.rarity && <span> | <strong>Rarità:</strong> {translateRarity(cardMetadata.rarity)}</span>}
            </div>
          )}
        </div>
        
        <div className="auction-details">
          <p>
            <span>Offerta Attuale:</span>
            <span>
              <span className="patapizza-icon"></span>
              {auction.current_bid.toLocaleString()}
              {auction.highest_bidder_username && (
                <span className="current-bidder"> da {auction.highest_bidder_username}</span>
              )}
            </span>
          </p>
          <p>
            <span>Tempo Rimasto:</span>
            <span>{timeLeft}</span>
          </p>
          {auction.highest_bidder_username && (
            <p>
              <span>In Testa:</span>
              <span>{auction.highest_bidder_username}</span>
            </p>
          )}
        </div>

        {error && <div className="error-message" style={{ fontSize: '0.8rem' }}>{error}</div>}

        {timeLeft !== 'Terminata' && (
          <form onSubmit={handleBid} className="bid-section">
            <input
              type="number"
              className="bid-input"
              placeholder={`Min: ${auction.current_bid + 1}`}
              value={bidAmount}
              onChange={(e) => setBidAmount(e.target.value)}
              min={auction.current_bid + 1}
              max={user.patapizze}
              disabled={loading}
            />
            <button 
              type="submit" 
              className="btn btn-bid"
              disabled={loading || !bidAmount}
            >
              {loading ? 'Offrendo...' : 'Offri'}
            </button>
          </form>
        )}
      </div>
      
      {auction.jpeg_filename && (
        <div className="card-image-container">
          <img 
            src={`${API_URL}/cards/${auction.jpeg_filename}`}
            alt={auction.name}
            className="card-image"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}
    </div>
  );
}

export default AuctionCard;